import { useMemo } from "react";
import { Order, OrderedProduct } from "src/shared/types";

type ReturnTotals = {
  qty: number;
  amount: number;
  refund: number;
  adjusted: number;
  remaining: number;
  totalPayment: number;
  adjustmentAmount: number;
  finalAmount: number;
  netReturn: number;
};

export const useReturnCalculation = (order: Order | null, returnQty: Record<number, number>, reverseDiscount: number): ReturnTotals => {
  return useMemo(() => {
    const empty: ReturnTotals = {
      qty: 0,
      amount: 0,
      refund: 0,
      adjusted: 0,
      remaining: 0,
      totalPayment: 0,
      adjustmentAmount: 0,
      finalAmount: 0,
      netReturn: 0,
    };

    if (!order) return empty;

    // New interface: financial fields in orders[0]
    const transaction = order.orders?.[0];
    const calOfFinalAmount = order.paymentHistory
      .filter((p) => p.paymentSource === "RETURN")
      .reduce(
        (acc, p) =>
          acc +
          Math.abs(
            Number(p.receivedAmount ?? 0)
          ) + Math.abs(Number(p.adjustAmount ?? 0) || 0),
        0
      );
    const finalAmount = Number(transaction?.finalAmount ?? 0) - calOfFinalAmount;

    const checkPayment =order.paymentHistory?.reduce((acc, p) => acc + Number(p.receivedAmount ?? 0), 0) ?? 0;

    // New interface: receivedAmount replaces ReceivedAmount
    const totalPayment = checkPayment > finalAmount ? finalAmount : checkPayment;

    // New paymentHistory schema has no adjustAmount — default 0
    const adjustmentAmount = 0;

    const remaining = finalAmount - totalPayment - adjustmentAmount;

    // New interface: products in orderDetails[0].products
    const products: OrderedProduct[] = order.orderDetails?.[0]?.products ?? [];

    let qty = 0;
    let amount = 0;

    products.forEach((p, i) => {
      const q = returnQty[i] || 0;
      // unitPrice replaces amount
      const price = Number(p.unitPrice ?? 0);
      qty += q;
      amount += q * price;
    });

    const netReturn = amount - (reverseDiscount || 0);
    const adjusted = Math.min(netReturn, remaining);
    const refund = netReturn - adjusted;

    return {
      qty,
      amount,
      refund,
      adjusted,
      remaining,
      totalPayment,
      adjustmentAmount,
      finalAmount,
      netReturn
    };
  }, [order, returnQty, reverseDiscount]);
};
